import {
  getDashboardLeagueRankingsControllerDatesQueryKey,
  getDashboardLeagueRankingsControllerHistoryQueryKey,
  getDashboardLeagueRankingsControllerListQueryKey,
} from "@/api/__generated__/dashboard-league-rankings/dashboard-league-rankings";
import { MessageKey } from "@/i18n";
import {
  LeagueRankingExportColumn,
  LeagueRankingExportColumnValues,
  LeagueRankingSortableColumn,
  LeagueRankingSortableColumnValues,
  LeagueRankingVisibility,
  RankingRefreshRate,
  RankingUpdateDayOfWeek,
  RankingUpdateMonthAnchor,
} from "@/types";

/** Translated labels for the three-layer `RankingRefreshRate` enum. */
export const RANKING_REFRESH_RATE_LABELS: { [key in RankingRefreshRate]: MessageKey } = {
  every_week: "leagueRanking.enums.refreshRate.every_week",
  every_month: "leagueRanking.enums.refreshRate.every_month",
  custom: "leagueRanking.enums.refreshRate.custom",
};

export const RANKING_UPDATE_DAY_OF_WEEK_LABELS: {
  [key in RankingUpdateDayOfWeek]: MessageKey;
} = {
  Monday: "leagueRanking.enums.dayOfWeek.monday",
  Tuesday: "leagueRanking.enums.dayOfWeek.tuesday",
  Wednesday: "leagueRanking.enums.dayOfWeek.wednesday",
  Thursday: "leagueRanking.enums.dayOfWeek.thursday",
  Friday: "leagueRanking.enums.dayOfWeek.friday",
  Saturday: "leagueRanking.enums.dayOfWeek.saturday",
  Sunday: "leagueRanking.enums.dayOfWeek.sunday",
};

export const RANKING_UPDATE_MONTH_ANCHOR_LABELS: {
  [key in RankingUpdateMonthAnchor]: MessageKey;
} = {
  first_day_of_month: "leagueRanking.enums.monthAnchor.first_day_of_month",
  last_day_of_month: "leagueRanking.enums.monthAnchor.last_day_of_month",
};

/** Translated labels for the two-layer `LeagueRankingVisibility` enum. */
export const LEAGUE_RANKING_VISIBILITY_LABELS: { [key in LeagueRankingVisibility]: MessageKey } = {
  public: "leagueRanking.enums.visibility.public",
  hidden: "leagueRanking.enums.visibility.hidden",
};

/**
 * Maximum number of ranking categories a single generate / schedule request may
 * target. Mirrors the backend `@ArrayMaxSize(50)` cap on the dashboard
 * league-rankings `generate` and `schedule` endpoints — keep the two in sync.
 */
export const MAX_RANKING_CATEGORIES_PER_UPDATE = 50;

/**
 * The Rankings tab's lightweight ranking-category list — read by the tab itself
 * (empty-league detection, public-view link, residence-filters opt-in), its
 * category select and the residence-filters availability hook. All three must
 * pass these exact params + options so react-query dedupes them into one
 * request. A league realistically holds a few dozen ranking categories (prod max
 * ~64), so the whole set fits one page and the select filters client-side.
 */
export const LEAGUE_RANKING_CATEGORY_LIST_PARAMS = { limit: 100, lightweight: true } as const;
export const LEAGUE_RANKING_CATEGORY_LIST_QUERY_OPTIONS = { staleTime: 60_000 } as const;

/** Per-page options for the two tables. */
export const RANKINGS_PAGE_SIZE_OPTIONS = [10, 25, 50];
export const RANKINGS_HISTORY_PAGE_SIZE_OPTIONS = [5, 10, 25];

/**
 * Every query family the League Rankings mutations touch. Each entry is the
 * endpoint's URL prefix (derived from the generated key helper, never a
 * hardcoded string) so `invalidateQueries` matches the whole family regardless
 * of params. Reused by every mutation (generate / visibility / delete /
 * schedule) so a single change keeps them all in sync.
 */
export const LEAGUE_RANKINGS_INVALIDATE_KEYS = [
  getDashboardLeagueRankingsControllerListQueryKey({
    leagueId: "",
    rankingCategoryId: "",
  }).slice(0, 1),
  getDashboardLeagueRankingsControllerHistoryQueryKey({ leagueId: "" }).slice(0, 1),
  getDashboardLeagueRankingsControllerDatesQueryKey({
    leagueId: "",
    rankingCategoryId: "",
  }).slice(0, 1),
] as const;

/**
 * Maximum number of automatic-update rules a single league may hold. Mirrors the
 * backend cap — an over-cap create is a 409 (`LEAGUE_RANKING_RULES_LIMIT_REACHED`)
 * — and drives the disabled state of the "Add new rule" button.
 */
export const MAX_LEAGUE_RANKING_RULES = 10;

/** Sortable columns of the Rankings leaderboard, in table order. */
export const LEAGUE_RANKING_SORTABLE_COLUMNS: readonly LeagueRankingSortableColumn[] =
  LeagueRankingSortableColumnValues;

/**
 * Column the leaderboard sorts by until the URL says otherwise. Shared so the
 * table and the header's export button seed `useTableSortQueryState` with the
 * same default — they read the same `?sortBy`, and a mismatch here would make
 * them disagree while the param is absent.
 */
export const DEFAULT_LEAGUE_RANKING_SORT_COLUMN: LeagueRankingSortableColumn = "position";

/** Translated labels for the sortable leaderboard columns. */
export const LEAGUE_RANKING_SORTABLE_COLUMN_LABELS: {
  [key in LeagueRankingSortableColumn]: MessageKey;
} = {
  position: "leagueRanking.columns.position",
  points: "leagueRanking.columns.points",
};

/** Selectable export columns, in the order the exported file lays them out. */
export const LEAGUE_RANKING_EXPORT_COLUMNS: readonly LeagueRankingExportColumn[] =
  LeagueRankingExportColumnValues;

/** Translated labels for the league-rankings export columns. */
export const LEAGUE_RANKING_EXPORT_COLUMN_LABELS: {
  [key in LeagueRankingExportColumn]: MessageKey;
} = {
  rankingCategory: "ranking.columns.rankingCategory",
  date: "leagueRanking.columns.date",
  place: "leagueRanking.columns.position",
  previousPlace: "leagueRanking.columns.previousPosition",
  name: "general.columns.name",
  surname: "general.columns.surname",
  team: "team.columns.teamName",
  nation: "user.export.columns.nation",
  birthDate: "user.export.columns.birthDate",
  club: "general.columns.club",
  points: "leagueRanking.columns.points",
  previousPoints: "leagueRanking.columns.previousPoints",
  tournaments: "leagueRanking.columns.tournaments",
};
